/**
 * Expanding Timeline — assets/expanding-timeline.js
 * Vanilla JS, IIFE. Loaded with `defer` from sections/expanding-timeline.liquid.
 *
 * Desktop: active card expands to N% of the row, others share the rest.
 *          Tab clicks, arrow clicks, and inactive-card clicks all switch
 *          which era is active.
 * Mobile:  cards become a horizontal swipe carousel — each card occupies
 *          100% of the viewport width. Swipe / tab / dot click syncs the
 *          active state. CSS overrides JS-set flex values via !important.
 */
(function () {
  'use strict';

  function init() {
    document.querySelectorAll('[data-etl]').forEach(initTimeline);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function initTimeline(root) {
    var tabs          = Array.from(root.querySelectorAll('[data-etl-tab]'));
    var cardsTrack    = root.querySelector('[data-etl-cards]');
    var cards         = Array.from(root.querySelectorAll('[data-etl-card]'));
    var captions      = Array.from(root.querySelectorAll('[data-etl-caption]'));
    var captionsTrack = root.querySelector('[data-etl-captions]');
    var prevBtn       = root.querySelector('[data-etl-prev]');
    var nextBtn       = root.querySelector('[data-etl-next]');
    var mobileDots    = Array.from(root.querySelectorAll('[data-etl-mobile-dot]'));

    var blockCount  = parseInt(root.dataset.blockCount, 10) || cards.length;
    var activeWidth = parseInt(root.dataset.activeWidth, 10) || 60;
    var loop        = root.dataset.loop === 'true';

    if (blockCount === 0 || cards.length === 0) return;

    var currentIndex = 0;
    var mobileQuery  = window.matchMedia('(max-width: 749px)');
    var isScrollSyncing = false;

    function setActive(index) {
      if (index < 0)            index = loop ? blockCount - 1 : 0;
      if (index >= blockCount)  index = loop ? 0 : blockCount - 1;
      currentIndex = index;

      tabs.forEach(function (tab, i) {
        tab.classList.toggle('is-active', i === index);
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });

      cards.forEach(function (card, i) {
        card.classList.toggle('is-active', i === index);
      });

      captions.forEach(function (cap, i) {
        cap.classList.toggle('is-active', i === index);
      });

      mobileDots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
      });

      if (mobileQuery.matches) {
        // Scroll the active card and caption into view
        if (cardsTrack && cards[index]) {
          isScrollSyncing = true;
          cardsTrack.scrollTo({ left: cards[index].offsetLeft, behavior: 'smooth' });
        }
        if (captionsTrack && captions[index]) {
          captionsTrack.scrollTo({ left: captions[index].offsetLeft, behavior: 'smooth' });
        }
        // Allow scroll listener to resume after the smooth scroll settles
        window.setTimeout(function () { isScrollSyncing = false; }, 500);
      } else {
        applyDesktopWidths();
      }
    }

    function applyDesktopWidths() {
      if (blockCount === 1) {
        if (cards[0])    cards[0].style.flex    = '0 0 100%';
        if (captions[0]) captions[0].style.flex = '0 0 100%';
        return;
      }

      var inactiveCount = blockCount - 1;
      var activeShare   = activeWidth / 100;
      var inactiveShare = inactiveCount > 0 ? (1 - activeShare) / inactiveCount : 0;

      // Row gap (cards) — read live so it follows merchant settings
      var cardGapPx = 0;
      if (cardsTrack) {
        cardGapPx = parseFloat(window.getComputedStyle(cardsTrack).gap) || 0;
      }
      var totalCardGapPx = cardGapPx * (blockCount - 1);

      cards.forEach(function (card, i) {
        var share = (i === currentIndex) ? activeShare : inactiveShare;
        card.style.flex = '0 0 calc(' + (share * 100) + '% - ' + (share * totalCardGapPx) + 'px)';
      });

      // Captions row gap may differ — read it independently
      var captionGapPx = cardGapPx;
      if (captionsTrack) {
        captionGapPx = parseFloat(window.getComputedStyle(captionsTrack).gap) || cardGapPx;
      }
      var totalCaptionGapPx = captionGapPx * (blockCount - 1);

      captions.forEach(function (cap, i) {
        var share = (i === currentIndex) ? activeShare : inactiveShare;
        cap.style.flex = '0 0 calc(' + (share * 100) + '% - ' + (share * totalCaptionGapPx) + 'px)';
      });
    }

    function clearDesktopWidths() {
      cards.forEach(function (card) { card.style.flex = ''; });
      captions.forEach(function (cap) { cap.style.flex = ''; });
    }

    // ── Tab clicks ──
    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function (e) {
        e.preventDefault();
        setActive(i);
      });
    });

    // ── Card clicks: only switch when inactive; let the link work when active ──
    cards.forEach(function (card, i) {
      card.addEventListener('click', function (e) {
        if (i === currentIndex) return;
        // Inactive card — block any inner link nav and just activate
        e.preventDefault();
        setActive(i);
      });
    });

    // ── Arrows ──
    if (prevBtn) prevBtn.addEventListener('click', function () { setActive(currentIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { setActive(currentIndex + 1); });

    // ── Mobile dots ──
    mobileDots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { setActive(i); });
    });

    // ── Mobile scroll sync: when user swipes cards, update active state ──
    if (cardsTrack) {
      var rafId = null;
      cardsTrack.addEventListener('scroll', function () {
        if (!mobileQuery.matches || isScrollSyncing) return;
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(function () {
          if (!cards[0]) return;
          var gapPx = parseFloat(getComputedStyle(cardsTrack).gap) || 0;
          var step = cards[0].offsetWidth + gapPx;
          if (step <= 0) return;
          var newIdx = Math.round(cardsTrack.scrollLeft / step);
          if (newIdx < 0) newIdx = 0;
          if (newIdx >= blockCount) newIdx = blockCount - 1;
          if (newIdx === currentIndex) return;

          currentIndex = newIdx;
          tabs.forEach(function (tab, i) {
            tab.classList.toggle('is-active', i === newIdx);
            tab.setAttribute('aria-selected', i === newIdx ? 'true' : 'false');
          });
          cards.forEach(function (card, i) { card.classList.toggle('is-active', i === newIdx); });
          captions.forEach(function (cap, i) { cap.classList.toggle('is-active', i === newIdx); });
          mobileDots.forEach(function (dot, i) { dot.classList.toggle('is-active', i === newIdx); });

          if (captionsTrack && captions[newIdx]) {
            captionsTrack.scrollTo({ left: captions[newIdx].offsetLeft, behavior: 'smooth' });
          }
        });
      });
    }

    // ── Breakpoint switch ──
    function handleBreakpoint() {
      if (mobileQuery.matches) {
        clearDesktopWidths();
      } else {
        applyDesktopWidths();
      }
    }
    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', handleBreakpoint);
    } else if (mobileQuery.addListener) {
      mobileQuery.addListener(handleBreakpoint);
    }

    // Recompute heights on viewport resize (debounced)
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(handleBreakpoint, 150);
    });

    // ── Keyboard navigation on tabs (ArrowLeft / ArrowRight) ──
    tabs.forEach(function (tab, i) {
      tab.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          var next = (i + 1) % blockCount;
          tabs[next].focus();
          setActive(next);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          var prev = (i - 1 + blockCount) % blockCount;
          tabs[prev].focus();
          setActive(prev);
        }
      });
    });

    // Initial state
    handleBreakpoint();
    setActive(0);
  }
})();
